import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Avatar, Box, Group, Skeleton, Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { PreviewDevice } from './EmailPreview.types';

interface Props {
	html: string;
	device: PreviewDevice;
	subject?: string;
	recipient?: string;
	loading?: boolean;
	error?: boolean;
	height?: number;
}

const DESKTOP_WIDTH = 600;
const MOBILE_WIDTH = 375;
const MIN_FRAME_HEIGHT = 240;
const MAX_FRAME_HEIGHT = 4000;

const Card = styled.div`
	overflow: hidden;
	border: 1px solid var(--mantine-color-gray-4);
	border-radius: var(--mantine-radius-md);
	background: var(--mantine-color-white);
`;

const Frame = styled.iframe<{ $height: number }>`
	display: block;
	width: 100%;
	height: ${({ $height }) => $height}px;
	border: none;
	background: var(--mantine-color-white);
`;

const ErrorState = styled.div<{ $height: number }>`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: ${({ $height }) => $height}px;
	border: 1px solid var(--mantine-color-gray-4);
	border-radius: var(--mantine-radius-md);
	background: var(--mantine-color-gray-0);
`;

const InboxHeader = ({ subject, recipient }: { subject: string; recipient?: string }) => {
	const tMipManage = useTranslationMipManage();
	return (
		<Group
			gap="sm"
			wrap="nowrap"
			p="sm"
			style={{ borderBottom: '1px solid var(--mantine-color-gray-4)' }}
		>
			<Avatar radius="xl" size={32} color="dark">
				D
			</Avatar>
			<Box style={{ flex: 1, minWidth: 0 }}>
				<Group gap={6} wrap="nowrap" justify="space-between">
					<Text fz="0.75rem" c="gray.9" truncate>
						Dealstack
					</Text>
					<Text fz="0.75rem" c="gray.9" style={{ flexShrink: 0 }} truncate>
						{tMipManage('email_templates.preview.to_recipient', {
							recipient:
								recipient ||
								tMipManage('email_templates.preview.recipient_default'),
						})}
					</Text>
				</Group>
				<Text fw={600} fz="0.8125rem" truncate title={subject}>
					{subject || tMipManage('email_templates.preview.no_subject')}
				</Text>
			</Box>
		</Group>
	);
};

export const EmailPreview = ({
	html,
	device,
	subject = '',
	recipient,
	loading,
	error,
	height = 520,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const frameRef = useRef<HTMLIFrameElement>(null);
	const [frameHeight, setFrameHeight] = useState(height);

	// body.scrollHeight, not documentElement: that one stretches to the frame.
	const measureFrame = useCallback(() => {
		const body = frameRef.current?.contentDocument?.body;
		if (!body) {
			return;
		}
		const styles = body.ownerDocument.defaultView?.getComputedStyle(body);
		const margins = styles
			? Number.parseFloat(styles.marginTop) + Number.parseFloat(styles.marginBottom)
			: 0;
		const measured = Math.ceil(body.scrollHeight + (Number.isNaN(margins) ? 0 : margins));
		setFrameHeight((previous) => {
			const next = Math.min(Math.max(MIN_FRAME_HEIGHT, measured), MAX_FRAME_HEIGHT);
			return Math.abs(next - previous) > 2 ? next : previous;
		});
	}, []);

	useEffect(() => {
		const body = frameRef.current?.contentDocument?.body;
		if (!body || typeof ResizeObserver === 'undefined') {
			return;
		}
		const observer = new ResizeObserver(measureFrame);
		observer.observe(body);
		return () => observer.disconnect();
	}, [device, html, measureFrame]);

	const body = () => {
		if (loading) {
			return <Skeleton height={height} radius="md" />;
		}
		if (error && !html) {
			return (
				<ErrorState $height={height}>
					<Stack gap={4} align="center" px="lg">
						<Text fw={600} size="md">
							{tMipManage('email_templates.preview.unavailable.title')}
						</Text>
						<Text size="md" c="gray.9" ta="center">
							{tMipManage('email_templates.preview.unavailable.message')}
						</Text>
					</Stack>
				</ErrorState>
			);
		}

		const previewStyle = '<style>body{padding:1rem!important;box-sizing:border-box}</style>';
		const srcDoc = html.includes('</head>')
			? html.replace('</head>', `${previewStyle}</head>`)
			: `${previewStyle}${html}`;

		return (
			<Card>
				<InboxHeader subject={subject} recipient={recipient} />
				<Frame
					ref={frameRef}
					$height={frameHeight}
					title={tMipManage('email_templates.preview.frame_title')}
					srcDoc={srcDoc}
					sandbox="allow-same-origin"
					onLoad={measureFrame}
				/>
			</Card>
		);
	};

	return (
		<Box
			style={{
				width: '100%',
				maxWidth: device === 'mobile' ? MOBILE_WIDTH : DESKTOP_WIDTH,
				marginInline: 'auto',
				transition: 'max-width 150ms ease',
			}}
		>
			{body()}
		</Box>
	);
};
